import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import EditContainer from "src/components/Shared/Containers/EditContainer";
import Button from "src/components/Shared/Buttons/Button";
import NotificationAddSlider from "./NotificationAddSlider";
import NotificationDeleteModal from "./NotificationDeleteModal";
import { CheckIcon, NoSymbolIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAsyncAction } from "src/hooks/useAsyncAction";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import PaginationFooter from "src/components/Shared/PaginationFooter";
import Table from "src/components/Shared/Table/Table";
import TRHeader from "src/components/Shared/Table/TRHeader";
import Search from "src/components/Shared/Search";
import TableOptions from "src/components/Shared/TableOptions";
import NotificationEntry from "./NotificationEntry";
import { useHasChanges } from "src/hooks/useHasChanges";
import useFetch from "src/hooks/useFetch";

const NotificationList = () => {
  const formJson = {
    status: false,
    content: "",
    variant: "default",
    background_color: "#ffffff",
    text_color: "#000000",
    client_ids: [],
  };

  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  const [notifications, setNotifications] = useState([]);
  const [meta, setMeta] = useState(null);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [formData, setFormData] = useState(formJson);
  const [initialFormData, setInitialFormData] = useState(formJson);

  const hasChanges = useHasChanges(formData, initialFormData);

  const [deleteId, setDeleteId] = useState(null);

  const {
    response: { data: clients },
  } = useFetch(`/clients`);

  const {
    loading: submitting,
    success: submitted,
    execute: manageNotification,
  } = useAsyncAction({
    onSuccess: () => {
      getNotificationList();
      setInitialFormData({ ...formData });
      setIsAddModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const getNotificationList = useCallback(async () => {
    try {
      setNotificationsLoaded(false);

      const res = await apiRequest("get", `/notifications`, {
        queries: {
          limit: limit || 20,
          offset: offset || 0,
          keyword: keyword || "",
        },
      });

      if (res.data.status === 200) {
        setNotifications(res.data.data || []);
        setMeta(res.data.meta);
      } else {
        setNotifications([]);
        setMeta(null);
      }
    } catch (error) {
    } finally {
      setNotificationsLoaded(true);
    }
  }, [limit, offset, keyword]);

  const handlePaginationChange = ({ limit, offset }) => {
    setLimit(limit);
    setOffset(offset);
  };

  useEffect(() => {
    getNotificationList();
  }, [getNotificationList]);

  const onSubmit = ({ reset_logs = false }) => {
    manageNotification(async () => {
      const isEditing = !!formData?._id;
      const url = `/notifications${isEditing ? "/:notification_id" : ""}`;

      const method = isEditing ? "PUT" : "POST";

      await apiRequest(method, url, {
        body: { ...formData, reset_logs },
        params: { notification_id: formData?._id },
      });
    });
  };

  const openAddModal = () => {
    setFormData(formJson);
    setInitialFormData(formJson);
    setIsAddModalOpen(true);
  };

  const handleEdit = (notification) => {
    setFormData(notification);
    setInitialFormData(notification);
    setIsAddModalOpen(true);
  };

  return (
    <EditContainer
      fullWidth
      title="Notification Banner"
      preview={{
        style: notifications?.find((item) => item?.status)?.content ? "success" : "default",
        isSuccess: notifications?.find((item) => item?.status)?.content,
        text: notifications?.find((item) => item?.status)?.content ? (
          <>
            <CheckIcon className="h-5 w-5" /> Enabled
          </>
        ) : (
          <>
            <NoSymbolIcon className="h-5 w-5" /> Disabled
          </>
        ),
      }}
      childClasses="!pl-0"
      defaultOptions={{
        onSuccessButtonVisible: false,
        onCancelButtonVisible: false,
      }}>
      <div className="animate-fade-in mr-2 max-w-7xl space-y-5">
        <TableOptions
          leftContent={
            <Search
              keyword={keyword}
              setKeyword={(val) => {
                setOffset(0);
                setKeyword(val);
              }}
              placeholder={"Search"}
              inline={true}
            />
          }
          rightContent={
            <div className="flex gap-2">
              <Button
                version="secondary"
                color="default"
                onClick={openAddModal}
                disabled={submitting}
                success={submitted}
                loading={submitting}>
                <PlusIcon className="h-4 w-4" />
                Add Notification
              </Button>
            </div>
          }
        />

        <Table
          tableHeader={
            <TRHeader>
              <th className="hidden w-[350px] rounded-tl-lg py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:table-cell sm:pl-6">Notification</th>
              <th className="hidden w-[190px] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">Clients</th>
              <th className="hidden w-[190px] px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">Variant</th>
              <th className="w-[190px] px-4 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th className="w-[190px] px-4 py-3.5 text-left text-sm font-semibold text-gray-900">Created At</th>
              <th className="w-[100px] px-2 py-3.5 pr-4 text-right text-sm font-semibold text-gray-900"></th>
            </TRHeader>
          }
          colSpan="5"
          loaded={notificationsLoaded}
          dataExists={notifications?.length > 0}>
          {notifications?.map((notification, i) => {
            return (
              <NotificationEntry
                key={notification?._id}
                clients={clients?.filter((client) => notification?.client_ids?.includes(client?._id))?.map((client) => ({ ...client, name: client?.company_name })) || []}
                handleEdit={handleEdit}
                setDeleteId={setDeleteId}
                notification={notification}
                refreshList={getNotificationList}
              />
            );
          })}
        </Table>
        <PaginationFooter
          itemName="Notification record"
          limit={limit}
          offset={offset}
          count={meta?.count}
          onChange={handlePaginationChange}
        />
      </div>

      <NotificationAddSlider
        isOpen={isAddModalOpen}
        formData={formData}
        clients={clients}
        extraProps={{ onSubmit, submitting, submitted }}
        setFormData={setFormData}
        handleClose={() => {
          setIsAddModalOpen(false);
        }}
        footerComponent={
          <div className="flex justify-end gap-x-3 ">
            {hasChanges && (
              <Button
                version="primary"
                className="animate-fade-in !h-11"
                type="button"
                onClick={() => onSubmit(false)}
                disabled={submitting}
                loading={submitting}
                success={submitted}>
                <CheckIcon className="h-5 w-5" />
                Save
              </Button>
            )}
            <Button
              version="gray"
              className="!h-11"
              onClick={() => {
                setIsAddModalOpen(false);
              }}>
              <XMarkIcon className="h-5 w-5" />
              Close
            </Button>
          </div>
        }
      />

      <NotificationDeleteModal
        deleteId={deleteId}
        setDeleteId={setDeleteId}
        onSuccess={() => {
          setDeleteId(null);
          getNotificationList();
        }}
      />
    </EditContainer>
  );
};

const mapStateToProps = (state) => ({
  site: state?.site,
  workspaceDetails: state?.workspaceDetails,
});

export default connect(mapStateToProps, {})(NotificationList);
